import React, { useEffect } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useForm from '@afosto/utils-react/validation/useForm';
import useDataSourceContext from '../DataSource/useDataSourceContext';
import Form from '../Form';
import FormDrawer from '../FormDrawer';
import FormulaField from '../FormulaField';
import TextField from '../TextField';
import translations from './translations';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import dasherize from '@afosto/utils/string/dasherize';
import { addColumnValidationSchema } from './validations';

const DataGridAddColumn = () => {
  const intl = useIntl();
  const {
    showAddColumn,
    setShowAddColumn,
    setShowColumnsSelection,
    dataProperties = {},
    views = {},
  } = useDataSourceContext();
  const { properties } = dataProperties;
  const { getActiveView, defaultView, setDefaultView, updateView } = views;

  /**
   * Close the add column drawer.
   */
  const handleClose = () => {
    setShowAddColumn(false);
  };

  /**
   * Create the new formula column.
   * @param {string} name
   * @param {string} formula
   */
  const createColumn = async ({ name, formula }) => {
    try {
      const activeView = getActiveView();
      const customProperty = {
        key: `${dasherize(name)}-${Date.now()}`,
        name,
        value: formula,
        type: 'formula',
      };

      if (activeView && activeView.key === defaultView.key) {
        setDefaultView({
          ...activeView,
          customProperties: [...(activeView.customProperties || []), customProperty],
        });
      } else if (activeView) {
        updateView({
          ...activeView,
          customProperties: [...(activeView.customProperties || []), customProperty],
        });
      }

      setShowAddColumn(false);
      setShowColumnsSelection(true);
    } catch (err) {
      // TODO: do something with error.
    }
  };

  /**
   * Return to the column selection drawer.
   */
  const handleCancel = () => {
    setShowAddColumn(false);
    setShowColumnsSelection(true);
  };

  const form = useForm({
    validationSchema: addColumnValidationSchema,
    defaultValues: {
      name: '',
      formula: '',
    },
    onCancel: handleCancel,
    onSubmit: async data => {
      await createColumn(data);
    },
  });
  const { clear: clearForm } = form;

  /**
   * Clear the form after closing the add column drawer.
   */
  useEffect(() => {
    if (!showAddColumn) {
      clearForm();
    }
  }, [showAddColumn, clearForm]);

  return (
    <FormDrawer
      isOpen={showAddColumn}
      onClose={handleClose}
      title={intl.formatMessage(translations.addColumn.title)}
      drawerWidth="440px"
    >
      <LoadingWrapper isLoading={form.isSubmitting}>
        <Form form={form}>
          <TextField
            name="name"
            label={intl.formatMessage(translations.addColumn.inputs.name.label)}
            autoFocus
          />
          <TextField
            name="formula"
            label={intl.formatMessage(translations.addColumn.inputs.formula.label)}
            multiline
            InputProps={{
              inputComponent: FormulaField,
              inputProps: {
                autoComplete: 'off',
                suggestions: properties.map(property => ({
                  id: property.key,
                  display: property.name,
                })),
              },
            }}
          />
        </Form>
      </LoadingWrapper>
    </FormDrawer>
  );
};

export default DataGridAddColumn;
